import { useEffect, useState } from 'react';
import { errorMessage } from '../../shared/api/client';
import { Alert, Button, Card } from '../../shared/ui/components';
import { isEwThumbnail, localApi, localUrl, type LocalFile } from '../presentation/local';

const size = (bytes: number) => (bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`);
const KIND: Record<LocalFile['kind'], string> = { image: 'Imagen', video: 'Video', audio: 'Audio' };

/** Los archivos de la carpeta de esta computadora (solo dentro de la app KEORA Multimedia). */
export function useLocalFiles() {
  const [local] = useState(localApi);
  const [files, setFiles] = useState<LocalFile[] | null>(null);
  const [folder, setFolder] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!local) return;
    void local.list().then(setFiles).catch((e: unknown) => setError(errorMessage(e)));
    void local.folder().then(setFolder).catch(() => undefined);
  }, [local]);

  const run = (job: Promise<LocalFile[]>) => job.then(setFiles).catch((e: unknown) => setError(errorMessage(e)));
  return { local, files, setFiles, folder, error, setError, run };
}

export type LocalFiles = ReturnType<typeof useLocalFiles>;

/**
 * En la app KEORA Multimedia: imágenes, videos y audios guardados en una carpeta de esta
 * computadora. No se suben al servidor; aparecen entre los archivos con la marca «En esta
 * computadora» y en Proyectar se eligen en «En esta computadora». Para que los vean otros, «Subir a
 * la nube de la iglesia» (el mismo botón de la web) está junto a esta tarjeta.
 */
export function LocalFilesCard({ state }: { state: LocalFiles }) {
  const { local, files, setFiles, folder, error, setError, run } = state;
  const [cleaning, setCleaning] = useState(false);
  if (!local) return null;
  const thumbs = (files ?? []).filter((f) => isEwThumbnail(f.name));

  async function cleanThumbs() {
    if (!local || !window.confirm(`¿Quitar ${thumbs.length} miniaturas de EasyWorship? Son copias pequeñas que EasyWorship hace para sí mismo, no tus archivos.`)) return;
    setCleaning(true);
    try {
      let list: LocalFile[] = files ?? [];
      for (const f of thumbs) list = await local.remove(f.name);
      setFiles(list);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setCleaning(false);
    }
  }

  return (
    <Card title="En esta computadora">
      <p className="small muted">
        {files ? `${files.length} ${files.length === 1 ? 'archivo' : 'archivos'} en esta computadora. ` : ''}
        Se quedan solo aquí y aparecen abajo con la marca «En esta computadora». Para que los vean en otras computadoras, usa «Subir a la nube de la iglesia».
        {folder ? ` Carpeta: ${folder}` : ''}
      </p>
      {error ? <Alert>{error}</Alert> : null}
      <div className="actions">
        <Button onClick={() => void run(local.add())}>Guardar en esta computadora</Button>
        <Button variant="secondary" onClick={() => void local.openFolder()}>
          Abrir la carpeta
        </Button>
      </div>
      {thumbs.length > 0 ? (
        <Alert tone="info">
          <p>
            Hay {thumbs.length} {thumbs.length === 1 ? 'miniatura' : 'miniaturas'} de EasyWorship (los nombres largos con «.d=…w=…h=…»). No son tus archivos: EasyWorship las hace para sí mismo.
          </p>
          <Button variant="secondary" busy={cleaning} onClick={() => void cleanThumbs()}>
            Quitar las miniaturas
          </Button>
        </Alert>
      ) : null}
    </Card>
  );
}

/** Un archivo de esta computadora como tarjeta entre los archivos de Multimedia. */
export function LocalFileCard({ file: f, state }: { file: LocalFile; state: LocalFiles }) {
  const { local, run } = state;
  if (!local) return null;
  const src = localUrl(f.name);

  function rename() {
    const ext = /\.[^.]+$/.exec(f.name)?.[0] ?? '';
    const next = window.prompt('Nuevo nombre', f.name.slice(0, f.name.length - ext.length));
    if (next && next.trim() && local?.rename) void run(local.rename(f.name, next.trim()));
  }

  return (
    <li className="media-card card">
      {f.kind === 'image' ? (
        <img className="media-card__preview" src={src} alt={f.name} loading="lazy" />
      ) : f.kind === 'video' ? (
        <video className="media-card__preview" src={src} controls preload="metadata" aria-label={f.name} />
      ) : (
        <audio className="media-card__audio" src={src} controls preload="none" aria-label={f.name} />
      )}
      <div className="media-card__body">
        <strong className="media-card__name">{f.name}</strong>
        <span className="muted small">
          {KIND[f.kind]} · {size(f.size)}
        </span>
        <span className="small media-card__folder">💻 En esta computadora</span>
      </div>
      <div className="actions actions--wrap">
        {local.rename ? (
          <Button variant="ghost" onClick={rename}>
            Renombrar
          </Button>
        ) : null}
        <Button
          variant="ghost"
          onClick={() => {
            if (window.confirm(`¿Quitar «${f.name}» de esta computadora?`)) void run(local.remove(f.name));
          }}
        >
          Quitar
        </Button>
      </div>
    </li>
  );
}
